import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Avatar,
  Divider,
  IconButton,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LogoutIcon from '@mui/icons-material/Logout';

function CustomerAuth({ onAuthSuccess, onLogout }) {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [error, setError] = useState(null);

  // Registration form state
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    mobile: '',
    address: '',
  });

  // Login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
  });

  // Profile edit state
  const [profileData, setProfileData] = useState({
    name: '',
    mobile: '',
    address: '',
  });

  useEffect(() => {
    // Check if customer is already authenticated
    checkAuthStatus();

    // Wait for Google API to load
    const checkGoogleAPI = () => {
      if (window.google || (window.gapi && window.gapi.auth2)) {
        setGoogleLoading(false);
      } else {
        setTimeout(checkGoogleAPI, 100);
      }
    };

    checkGoogleAPI();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('customerToken');
      const customerData = localStorage.getItem('customerData');

      if (token && customerData) {
        const parsedCustomer = JSON.parse(customerData);

        // Verify token with backend
        const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/customer/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            setCustomer(result.customer);
            onAuthSuccess(result.customer, null, 'customer');
          }
        } else {
          // Token invalid, clear storage
          localStorage.removeItem('customerToken');
          localStorage.removeItem('customerData');
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('customerToken');
      localStorage.removeItem('customerData');
    }
  };

  const handleRegister = async () => {
    if (!registerData.name || !registerData.email || !registerData.password) {
      setError('Please fill in all required fields');
      return;
    }

    if (registerData.password !== registerData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (registerData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/customer/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: registerData.name,
          email: registerData.email,
          password: registerData.password,
          mobile: registerData.mobile,
          address: registerData.address,
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Store auth data
        localStorage.setItem('customerToken', result.token);
        localStorage.setItem('customerData', JSON.stringify(result.customer));

        setCustomer(result.customer);
        onAuthSuccess(result.customer, null, 'customer');

        // Reset form
        setRegisterData({
          name: '',
          email: '',
          password: '',
          confirmPassword: '',
          mobile: '',
          address: '',
        });
      } else {
        setError(result.message || 'Registration failed');
      }
    } catch (error) {
      setError('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!loginData.email || !loginData.password) {
      setError('Please enter email and password');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/customer/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: loginData.email,
          password: loginData.password,
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Store auth data
        localStorage.setItem('customerToken', result.token);
        localStorage.setItem('customerData', JSON.stringify(result.customer));

        setCustomer(result.customer);
        onAuthSuccess(result.customer, null, 'customer');

        // Reset form
        setLoginData({
          email: '',
          password: '',
        });
      } else {
        setError(result.message || 'Login failed');
      }
    } catch (error) {
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const CLIENT_SECRET = process.env.REACT_APP_GOOGLE_CLIENT_SECRET;

    if (!CLIENT_ID) {
      setError('Google Client ID not configured. Please check your .env file.');
      return;
    }

    if (googleLoading) {
      setError('Google Sign-In is still loading. Please wait a moment and try again.');
      return;
    }

    setGoogleLoading(true);
    setError(null);

    // Production-level OAuth flow with real token exchange
    if (window.google && window.google.accounts) {
      // Use Google Identity Services with proper OAuth flow
      const client = window.google.accounts.oauth2.initCodeClient({
        client_id: CLIENT_ID,
        scope: 'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile',
        ux_mode: 'popup',
        callback: async (response) => {
          if (response.code) {
            try {
              // Exchange authorization code for access token
              await exchangeCodeForTokens(response.code);
            } catch (error) {
              setError('Failed to complete authentication. Please try again.');
            }
          } else {
            setError('Google Sign-In failed. Please try again.');
          }
          setGoogleLoading(false);
        },
      });

      client.requestCode();
    } else if (window.gapi && window.gapi.auth2) {
      // Fallback to older gapi method with proper scopes
      window.gapi.auth2.init({
        client_id: CLIENT_ID,
        scope: 'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile'
      }).then(() => {
        window.gapi.auth2.getAuthInstance().signIn().then(
          (googleUser) => {
            const profile = googleUser.getBasicProfile();
            const authResponse = googleUser.getAuthResponse();

            const userData = {
              id: profile.getId(),
              name: profile.getName(),
              email: profile.getEmail(),
              imageUrl: profile.getImageUrl(),
              accessToken: authResponse.access_token,
              idToken: authResponse.id_token,
            };

            checkCustomerRegistration(userData);
          },
          (error) => {
            setError('Google Sign-In failed. Please try again.');
            setGoogleLoading(false);
          }
        );
      }).catch((error) => {
        setError('Failed to initialize Google Sign-In. Please check your configuration.');
        setGoogleLoading(false);
      });
    } else {
      setError('Google Sign-In is not loaded. Please refresh the page.');
      setGoogleLoading(false);
    }
  };

  const exchangeCodeForTokens = async (code) => {
    try {
      // This would typically be done on your backend
      // For now, we'll use a simplified approach
      const tokenResponse = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          client_secret: process.env.REACT_APP_GOOGLE_CLIENT_SECRET || '', // You might need to add this
          code: code,
          grant_type: 'authorization_code',
          redirect_uri: window.location.origin,
        }),
      });

      const tokens = await tokenResponse.json();

      if (tokens.access_token) {
        // Get user profile with access token
        const profileResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
          headers: {
            'Authorization': `Bearer ${tokens.access_token}`,
          },
        });

        const profile = await profileResponse.json();

        const userData = {
          id: profile.id,
          name: profile.name,
          email: profile.email,
          imageUrl: profile.picture,
          accessToken: tokens.access_token,
          idToken: tokens.id_token,
        };

        checkCustomerRegistration(userData);
      } else {
        setError('Failed to get access token. Please try again.');
      }
    } catch (error) {
      setError('Failed to complete Google Sign-In. Please try again.');
    }
  };

  const checkCustomerRegistration = async (userData) => {
    try {
      // Authenticate with backend as customer
      const authResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/customer/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          accessToken: userData.accessToken,
          profile: userData,
        }),
      });

      const authResult = await authResponse.json();

      if (authResult.success) {
        // Store auth data
        localStorage.setItem('customerToken', authResult.token);
        localStorage.setItem('customerData', JSON.stringify(authResult.customer));

        console.log('Google auth successful, customer data:', authResult.customer);
        setCustomer(authResult.customer);
        onAuthSuccess(authResult.customer, null, 'customer');
      } else {
        setError(authResult.message || 'Google authentication failed');
      }
    } catch (error) {
      setError('Failed to authenticate with backend. Please try again.');
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('customerToken');

      // Call logout endpoint
      await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/customer/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage
      localStorage.removeItem('customerToken');
      localStorage.removeItem('customerData');

      setCustomer(null);
      if (onLogout) {
        onLogout();
      }
    }
  };

  const handleUpdateProfile = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('customerToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/customer/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: profileData.name,
          mobile: profileData.mobile,
          address: profileData.address,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setCustomer(result.customer);
        localStorage.setItem('customerData', JSON.stringify(result.customer));
        setError('Profile updated successfully');
      } else {
        setError(result.message || 'Failed to update profile');
      }
    } catch (error) {
      setError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value, formType) => {
    if (formType === 'register') {
      setRegisterData(prev => ({ ...prev, [field]: value }));
    } else if (formType === 'login') {
      setLoginData(prev => ({ ...prev, [field]: value }));
    } else if (formType === 'profile') {
      setProfileData(prev => ({ ...prev, [field]: value }));
    }
  };

  // If customer is authenticated, show profile
  if (customer) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar
                  sx={{ width: 60, height: 60, mr: 2, bgcolor: 'primary.main' }}
                >
                  <PersonIcon sx={{ fontSize: 30 }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {customer.name || 'Customer'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Customer Account
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={handleLogout} color="error">
                <LogoutIcon />
              </IconButton>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Account Information
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmailIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Typography>{customer.email}</Typography>
              </Box>

              {customer.mobile && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PhoneIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Typography>{customer.mobile}</Typography>
                </Box>
              )}

              {customer.address && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LocationOnIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Typography>{customer.address}</Typography>
                </Box>
              )}

              {/* Customer Points Display */}
              <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.light', borderRadius: 2, cursor: 'pointer' }}
                   onClick={() => window.location.href = '/points'}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                  Loyalty Points
                </Typography>
                <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                  {customer.points_balance || 0} Points
                </Typography>
                <Typography variant="body2" sx={{ color: 'white', opacity: 0.9 }}>
                  Tier: {customer.points_tier || 'Bronze'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'white', opacity: 0.7, mt: 1, display: 'block' }}>
                  Click to view details & history
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Edit Profile
            </Typography>

            {error && (
              <Alert severity={error === 'Profile updated successfully' ? 'success' : 'error'} sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              <TextField
                label="Full Name"
                value={profileData.name || customer.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value, 'profile')}
                fullWidth
              />
              <TextField
                label="Mobile Number"
                value={profileData.mobile || customer.mobile || ''}
                onChange={(e) => handleInputChange('mobile', e.target.value, 'profile')}
                fullWidth
              />
              <TextField
                label="Address"
                value={profileData.address || customer.address || ''}
                onChange={(e) => handleInputChange('address', e.target.value, 'profile')}
                fullWidth
                multiline
                rows={3}
              />
            </Box>

            <Button
              variant="contained"
              onClick={handleUpdateProfile}
              disabled={loading}
              fullWidth
              sx={{ py: 1.5 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Update Profile'}
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Authentication forms
  return (
    <Box sx={{ maxWidth: 500, mx: 'auto', p: 2 }}>
      <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" sx={{ textAlign: 'center', mb: 3, fontWeight: 700 }}>
            Customer Portal
          </Typography>

          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ mb: 3 }}
            variant="fullWidth"
          >
            <Tab label="Sign In" />
            <Tab label="Sign Up" />
          </Tabs>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {activeTab === 0 && (
            // Login Form
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Email"
                type="email"
                value={loginData.email}
                onChange={(e) => handleInputChange('email', e.target.value, 'login')}
                fullWidth
                required
              />
              <TextField
                label="Password"
                type="password"
                value={loginData.password}
                onChange={(e) => handleInputChange('password', e.target.value, 'login')}
                fullWidth
                required
              />

              <Button
                variant="contained"
                onClick={handleLogin}
                disabled={loading}
                fullWidth
                sx={{ py: 1.5, mt: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Sign In'}
              </Button>

              <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                <Box sx={{ flex: 1, height: 1, background: '#e0e0e0' }} />
                <Typography sx={{ px: 2, color: '#666', fontSize: '0.9rem' }}>or</Typography>
                <Box sx={{ flex: 1, height: 1, background: '#e0e0e0' }} />
              </Box>

              <Button
                variant="outlined"
                onClick={handleGoogleSignIn}
                disabled={googleLoading}
                startIcon={<GoogleIcon />}
                fullWidth
                sx={{
                  py: 1.5,
                  borderColor: '#4285f4',
                  color: '#4285f4',
                  '&:hover': {
                    borderColor: '#3367d6',
                    backgroundColor: '#f8f9fa',
                  },
                }}
              >
                {googleLoading ? <CircularProgress size={20} /> : 'Continue with Google'}
              </Button>
            </Box>
          )}

          {activeTab === 1 && (
            // Registration Form
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Full Name"
                value={registerData.name}
                onChange={(e) => handleInputChange('name', e.target.value, 'register')}
                fullWidth
                required
              />
              <TextField
                label="Email"
                type="email"
                value={registerData.email}
                onChange={(e) => handleInputChange('email', e.target.value, 'register')}
                fullWidth
                required
              />
              <TextField
                label="Password"
                type="password"
                value={registerData.password}
                onChange={(e) => handleInputChange('password', e.target.value, 'register')}
                fullWidth
                required
                helperText="Minimum 6 characters"
              />
              <TextField
                label="Confirm Password"
                type="password"
                value={registerData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value, 'register')}
                fullWidth
                required
              />
              <TextField
                label="Mobile Number"
                value={registerData.mobile}
                onChange={(e) => handleInputChange('mobile', e.target.value, 'register')}
                fullWidth
              />
              <TextField
                label="Address"
                value={registerData.address}
                onChange={(e) => handleInputChange('address', e.target.value, 'register')}
                fullWidth
                multiline
                rows={3}
              />

              <Button
                variant="contained"
                onClick={handleRegister}
                disabled={loading}
                fullWidth
                sx={{ py: 1.5, mt: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Create Account'}
              </Button>

              <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                <Box sx={{ flex: 1, height: 1, background: '#e0e0e0' }} />
                <Typography sx={{ px: 2, color: '#666', fontSize: '0.9rem' }}>or</Typography>
                <Box sx={{ flex: 1, height: 1, background: '#e0e0e0' }} />
              </Box>

              <Button
                variant="outlined"
                onClick={handleGoogleSignIn}
                disabled={googleLoading}
                startIcon={<GoogleIcon />}
                fullWidth
                sx={{
                  py: 1.5,
                  borderColor: '#4285f4',
                  color: '#4285f4',
                  '&:hover': {
                    borderColor: '#3367d6',
                    backgroundColor: '#f8f9fa',
                  },
                }}
              >
                {googleLoading ? <CircularProgress size={20} /> : 'Sign Up with Google'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default CustomerAuth;